import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref } from 'vue';

import DepartmentTreeSelect from '@/components/DepartmentTreeSelect.vue';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { usePermissionComposable } from '@/composables/usePermission';
import { useUserComposable } from '@/composables/useUser';
import { generalApi } from '@/lib/api/general';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { i18n } from '@/plugins/vue-i18n';
import { readUserLocale } from '@/store/main/getters';
import { store } from '@/store/index';
import { formatDatePickerDay, formatToDate } from '@/utils/datetime';
import type {
  SupplierInquiryResponse,
  SupplierInquiryStatus,
  SupplierInquirySummaryResponse,
} from '@/interfaces/api/general/supplierInquiry';

type NotificationColor = 'success' | 'error' | 'warning' | 'info';

// Use `t` (not `tc`) so {param} placeholders are substituted from the args object.
type ChipStyle = { backgroundColor: string; color: string };

// Bg/fg pairs match the admin StatusChip (statusColors.ts in
// sds_manager_admin_frontend), which mirror the wireframe `.badge-*`
// rules — single source of truth across admin + customer surfaces.
// Solid pill colors per Figma (node 38513:51194). All chips use white text.
type FilterCapsule = {
  key: string;
  label: string;
  style?: ChipStyle;
  clear: () => void;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'SupplierInquiryPage',
  setup(__props) {

const $t = i18n.t.bind(i18n);

// Keys that already live in `lib/shared-messages.ts` are imported and reused
// (cancel / pageSelect / more / from / until / productCode / selectPlease /
// supplierInquiry / sdsId). Anything below is page-specific.
//
// Within-page duplicates collapsed:
//   filterStatusLabel + colStatus     → statusLabel        (ケミカン対応状況)
//   filterRequestedAt + colRequestedAt → requestedAtLabel  (依頼日)
//   filterObtainedAt  + colObtainedAt  → obtainedAtLabel   (完了日)
const m = defineMessages({
  // Header
  statusSectionTitle: 'サプライヤー問い合わせ状況',
  // NB: statusInfoLink + dialogTitle both render sharedMessages.siStatusInfoTitle;
  // closeBtn → sharedMessages.close; 担当部署 labels + 選択してください placeholders
  // reuse sharedMessages.department / sharedMessages.selectPlease; status-chip
  // labels come from shared-messages siStatus-prefixed keys. All to keep the
  // CI i18n duplicate check happy (single source of truth for reused strings).
  //
  // filterMenuDone is the "完了" (Done) button on the status filter dropdown.
  // Previously reused siStatusCompleted, but that shared key was removed when
  // PdM 2026-07-17 dropped the COMPLETED status entirely, so it lives here.
  filterMenuDone: '完了',
  statusInfoDialogLead:
    'サプライヤー問い合わせを依頼した後の現在の進捗状況（ステータス）を確認できます。',
  statusInfoNotObtainedTitle: 'ケミカンが依頼を受け付けました',
  statusInfoNotObtainedBody:
    '次のステップへ進むまでしばらくお待ちください。ユーザーによるキャンセルはこの段階でのみ可能です。',
  statusInfoContactedTitle: 'ケミカンがサプライヤーへ問い合わせました',
  statusInfoContactedBody: 'サプライヤーからの回答を待っています。',
  statusInfoObtainedTitle: 'サプライヤーからの回答を受け取りました',
  statusInfoObtainedBody:
    '内容は「ケミカンからのコメント」でご確認いただけます。SDSなどのファイルの提供があった場合は、あわせてダウンロードが可能です。',
  statusInfoNotApplicableTitle: 'サプライヤーからの回答が得られませんでした',
  statusInfoNotApplicableBody:
    '現行の内容が最新のため対応不要と判断された場合や、何らかの理由で回答を得られなかった場合を含みます。詳細は「ケミカンからのコメント」をご確認ください。',
  statusInfoCancelledTitle: 'ユーザーによって依頼が取り消されました',
  statusInfoCancelledBody: '手続きは終了しています。',
  // Search / filter
  searchPlaceholder: 'SDSを検索（SDSファイル名・サプライヤー名・製品コード）',
  searchBtn: '検索',
  clearFiltersBtn: '条件をクリアする',
  statusLabel: 'ケミカン対応状況',
  requestedAtLabel: '依頼日',
  obtainedAtLabel: '完了日',

  // Table-only labels
  colSupplier: 'サプライヤー名',

  empty: '依頼はまだありません',
  // Pagination
  pageOf: '{current} / {total}（全{count}件）',
  // Selection action bar
  // Download button label reuses sharedMessages.download ('ダウンロード') —
  // PdM 2026-07-22 shortened from '最新版SDSのダウンロード' since
  // サプライヤー問い合わせ answers may be any kind of file, not just SDS.
  selectAllAcrossPages: '全SDS {n}件をすべて選択',
  cancelBtn: '依頼をキャンセル',
  // Cancel dialog (Figma 38176:141354).
  //
  // PdM 2026-07-22 collapses the earlier single/multi title split into a
  // single title, and swaps SDS-flavored copy for file-type-agnostic
  // wording ('SDSの依頼' → '依頼', '以外のSDS' → '以外'). Body is now
  // always "{n}件の依頼が…" regardless of n.
  cancelTitle: '選択中の依頼をキャンセルしますか？',
  cancelBodyLead: '「依頼をキャンセルする」をクリックすると、',
  cancelBodyCount: '{n}件の依頼が',
  cancelBodyTail: 'キャンセルされます。',
  cancelBodyFollowup:
    'キャンセル後も、再度サプライヤー問い合わせの依頼をすることが可能です。',
  cancelWarning: '※「依頼済み」以外は依頼のキャンセルができません。',
  cancelOk: '依頼をキャンセルする',
  cancelSuccess: '依頼をキャンセルしました',
  cancelError: 'キャンセルに失敗しました',
  loadError: '読み込みに失敗しました',
  // Bulk-download confirmation dialog (Figma 38176:133878).
  //
  // PdM 2026-07-22 gives a simpler body than the Figma design — the
  // Figma mentions an email-URL delivery flow and a 3000-file cap that
  // don't exist in the current direct-ZIP implementation, so PdM's
  // shorter copy is used verbatim.
  downloadTitle: '選択中のファイルをダウンロードしますか？',
  downloadBody: '対応完了かつファイルがある{n}件がダウンロード対象です。',
  downloadWarning:
    '※対応完了ステータス以外、またはファイルがない依頼はダウンロードできません。',
  downloadOk: 'ダウンロードする',
  // Toast / error messages
  noDownloadableInSelection:
    '選択中の依頼にダウンロード可能なファイルはありません',
  noDownloadPermission: 'ダウンロードする権限がありません',
  downloadFailedFor: '{name} のダウンロードに失敗しました',
  bulkDownloadFailed: 'ダウンロードに失敗しました',
  // Selection footer
  selectionFooterLabel: 'このページ内の',
  selectionFooterSdsCount: 'SDS {n}件',
  selectionFooterSuffix: 'が選択されています。',
});

const STATUS_LABEL = computed<Record<SupplierInquiryStatus, string>>(() => ({
  NOT_OBTAINED: $t(sharedMessages.siStatusNotObtained),
  CONTACTED: $t(sharedMessages.siStatusContacted),
  OBTAINED: $t(sharedMessages.siStatusObtained),
  NOT_APPLICABLE: $t(sharedMessages.siStatusNotApplicable),
  CANCELLED: $t(sharedMessages.siStatusCancelled),
}));

const STATUS_CHIP: Record<SupplierInquiryStatus, ChipStyle> = {
  NOT_OBTAINED: { backgroundColor: '#01B7D6', color: '#ffffff' },
  CONTACTED: { backgroundColor: '#5D87FF', color: '#ffffff' },
  OBTAINED: { backgroundColor: '#2CAC9F', color: '#ffffff' },
  NOT_APPLICABLE: { backgroundColor: '#FF5454', color: '#ffffff' },
  CANCELLED: { backgroundColor: '#949497', color: '#ffffff' },
};

// Status counter chips shown above the table, in this order.
const COUNTER_STATUSES: SupplierInquiryStatus[] = [
  'NOT_OBTAINED',
  'CONTACTED',
  'OBTAINED',
  'NOT_APPLICABLE',
  'CANCELLED',
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

// Role gate (matrix fixed in PR #2749 review, 2026-08): downloading results
// requires SDS_DOWNLOAD — 閲覧者 lacks it. Department-independent, so this
// single check gates both the selection-footer bulk download and the
// row-hover single download. Creation gating lives on the SDS detail page
// (SupplierInquiryRequestButton / SdsTableDrawer).
const { hasPermission, PERMISSIONS } = usePermissionComposable();
const canDownloadResults = computed(() =>
  hasPermission(PERMISSIONS.SDS_DOWNLOAD)
);

const loading = ref(false);
const items = ref<SupplierInquiryResponse[]>([]);

// Hover-actions overlay: position: absolute inside the .table-hover-host
// wrapper so it scrolls with the table naturally (V2 pattern — actions
// live inside the row's coordinate space rather than being pinned to
// the viewport, which was the source of the "overlay stays put while
// user scrolls" bug).
const tableWrapper = ref<HTMLElement | null>(null);
const hoveredItem = ref<SupplierInquiryResponse | null>(null);
const overlayTop = ref(0);
const overlayHeight = ref(0);
// Reference to the currently-hovered <tr>. We manually toggle a class
// on it so the row keeps its hover-tinted background even when the
// mouse moves onto the sibling overlay (V2 has this "for free" because
// its overlay is a <td> inside the row).
const hoveredRowEl = ref<HTMLElement | null>(null);

const setHoveredRow = (tr: HTMLElement | null) => {
  if (hoveredRowEl.value && hoveredRowEl.value !== tr) {
    hoveredRowEl.value.classList.remove('row-hover-forced');
  }
  hoveredRowEl.value = tr;
  if (tr) tr.classList.add('row-hover-forced');
};

const onTableMouseOver = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  if (target.closest('.hover-actions-overlay')) return;
  const tr = target.closest('tbody tr') as HTMLElement | null;
  if (!tr || !tableWrapper.value?.contains(tr)) {
    hoveredItem.value = null;
    setHoveredRow(null);
    return;
  }
  const tbody = tr.parentElement;
  if (!tbody) return;
  const idx = Array.from(tbody.children).indexOf(tr);
  if (idx < 0 || idx >= pageItems.value.length) {
    hoveredItem.value = null;
    setHoveredRow(null);
    return;
  }
  hoveredItem.value = pageItems.value[idx];
  setHoveredRow(tr);
  const rect = tr.getBoundingClientRect();
  const hostRect = tableWrapper.value.getBoundingClientRect();
  overlayTop.value = rect.top - hostRect.top;
  overlayHeight.value = rect.height;
};

const onTableMouseLeave = () => {
  hoveredItem.value = null;
  setHoveredRow(null);
};

// The hover-actions overlay sits on top of the last column with
// `position: absolute`, so wheel events fired over the buttons don't
// naturally reach the v-data-table's internal horizontal scroller —
// horizontally-scrolling the table gets blocked as soon as the cursor
// crosses onto the overlay. Forward the wheel delta manually.
const onOverlayWheel = (e: WheelEvent) => {
  const host = tableWrapper.value;
  if (!host) return;
  const scroller = host.querySelector<HTMLElement>('.v-data-table__wrapper');
  if (!scroller) return;
  // Vertical wheel: let the browser scroll the page as usual — the
  // table itself doesn't scroll vertically inside the wrapper on this
  // page. Horizontal wheel (deltaX, or shift+wheel which some
  // trackpads emit as deltaY): forward to the table scroller.
  //
  // Trackpads emit tiny incidental deltaX during vertical gestures.
  // Only hijack when the horizontal component clearly dominates —
  // otherwise the overlay would block vertical page scroll whenever
  // the cursor happens to be on top of the hover buttons.
  const dx = e.deltaX || (e.shiftKey ? e.deltaY : 0);
  const dy = e.shiftKey ? 0 : e.deltaY;
  if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 2) {
    scroller.scrollLeft += dx;
    e.preventDefault();
  }
};
const total = ref(0);
const summary = ref<SupplierInquirySummaryResponse | null>(null);

const searchQuery = ref('');
// The committed value is what's actually sent to the API and shown in the
// active-filter capsule row. Live typing into `searchQuery` should not
// surface the filter — that only happens once the user clicks 検索 or
// presses Enter.
const committedSearchQuery = ref('');
const statusFilter = ref<SupplierInquiryStatus[]>([]);
// Staged selection while the status menu is open — only commits on "完了".
const statusFilterDraft = ref<SupplierInquiryStatus[]>([]);
const isOpenStatusMenu = ref(false);
const requestedFrom = ref('');
const requestedTo = ref('');
const obtainedFrom = ref('');
const obtainedTo = ref('');
const departmentFilter = ref<string[]>([]);
const requesterFilter = ref<string[]>([]);

// 担当部署 / 依頼者 pickers — the composables cache the underlying store
// state so the fetch on mount below is a no-op if another page already
// populated the store this session.
const {
  companyDepartments,
  getCompanyDepartments,
  departmentTree,
  getCompanyDepartmentTree,
} = useDepartmentComposable();
const { users: companyUsers, getCompanyUsers } = useUserComposable();

const onDepartmentFilterChange = (uuids: string[]) => {
  departmentFilter.value = uuids;
  onLocalFilterChange();
};

const requesterOptions = computed(() =>
  companyUsers.value.map((u) => ({
    value: u.uuid,
    text:
      `${u.last_name ?? ''} ${u.first_name ?? ''}`.trim() || u.email || u.uuid,
  }))
);

const isOpenRequestedFromPicker = ref(false);
const isOpenRequestedToPicker = ref(false);
const isOpenObtainedFromPicker = ref(false);
const isOpenObtainedToPicker = ref(false);

const STATUS_FILTER_OPTIONS: SupplierInquiryStatus[] = [
  'NOT_OBTAINED',
  'CONTACTED',
  'OBTAINED',
  'NOT_APPLICABLE',
  'CANCELLED',
];

const openStatusMenu = () => {
  statusFilterDraft.value = [...statusFilter.value];
  isOpenStatusMenu.value = true;
};

const toggleStatusDraft = (s: SupplierInquiryStatus) => {
  const idx = statusFilterDraft.value.indexOf(s);
  if (idx >= 0) statusFilterDraft.value.splice(idx, 1);
  else statusFilterDraft.value.push(s);
};

const clearStatusDraft = () => {
  statusFilterDraft.value = [];
};

const commitStatusFilter = () => {
  statusFilter.value = [...statusFilterDraft.value];
  isOpenStatusMenu.value = false;
  currentPage.value = 1;
  fetchAll();
};

const clearAllFilters = () => {
  searchQuery.value = '';
  committedSearchQuery.value = '';
  statusFilter.value = [];
  statusFilterDraft.value = [];
  requestedFrom.value = '';
  requestedTo.value = '';
  obtainedFrom.value = '';
  obtainedTo.value = '';
  departmentFilter.value = [];
  requesterFilter.value = [];
  currentPage.value = 1;
  fetchAll();
};

const locale = computed(() => readUserLocale(store));

// Use an array (not Set) — Vue 2.7 reactivity tracks arrays best.
const selectedIds = ref<string[]>([]);

const itemsPerPage = ref(50);
const currentPage = ref(1);
const sortBy = ref('requested_at');
const sortDesc = ref(true);

const cancelDialog = ref(false);
const cancelling = ref(false);
const downloadDialog = ref(false);
const downloading = ref(false);
// Per-row single-download in flight — drives the spinner on the hover
// Download button. Keyed by request id so the correct row's button
// shows the spinner if the user re-hovers a different row mid-flight.
const downloadingSingleId = ref<string | null>(null);
const statusInfoDialog = ref(false);

const notification = reactive<{
  visible: boolean;
  message: string;
  color: NotificationColor;
}>({ visible: false, message: '', color: 'success' });

const showNotification = (message: string, color: NotificationColor) => {
  notification.visible = false;
  notification.message = message;
  notification.color = color;
  requestAnimationFrame(() => {
    notification.visible = true;
  });
};

// ---------------------------------------------------------------------------
// Derived
// ---------------------------------------------------------------------------

const statusCount = (s: SupplierInquiryStatus): number =>
  summary.value?.by_status?.[s] ?? 0;

const formatDate = (d?: string | null): string =>
  formatToDate(d ?? null, { format: 'yyyy/MM/dd' }) || '-';

// 「ケミカンからのコメント」column. The API only returns customer_message for
// closed requests (対応完了 / 対応不可). For 対応不可 fall back to the 事由
// (not_applicable_reason) when the operator left the notify comment empty —
// the status-info dialog points the user at this column for 対応不可 (#4184).
const chemicanCommentFor = (r: SupplierInquiryResponse): string | null =>
  r.customer_message ||
  (r.status === 'NOT_APPLICABLE' ? r.not_applicable_reason : null) ||
  null;

const requesterLabel = (r: SupplierInquiryResponse): string => {
  const u = r.requested_by_user;
  return (
    `${u.last_name ?? ''} ${u.first_name ?? ''}`.trim() || u.email || u.uuid
  );
};

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / itemsPerPage.value))
);

const pageOptions = computed(() => {
  const tp = totalPages.value;
  const count = total.value;
  return Array.from({ length: tp }, (_, i) => ({
    value: i + 1,
    text: $t(m.pageOf, {
      current: i + 1,
      total: tp,
      count,
    }) as string,
  }));
});

const pageItems = computed(() => items.value);

// Capsules for filters currently applied (server-side).
const activeFilters = computed<FilterCapsule[]>(() => {
  const caps: FilterCapsule[] = [];
  if (committedSearchQuery.value) {
    caps.push({
      key: 'q',
      label: `${$t(m.searchBtn)}: ${committedSearchQuery.value}`,
      clear: () => {
        searchQuery.value = '';
        committedSearchQuery.value = '';
        fetchAll();
      },
    });
  }
  for (const s of statusFilter.value) {
    caps.push({
      key: `status:${s}`,
      label: STATUS_LABEL.value[s],
      style: STATUS_CHIP[s],
      clear: () => {
        statusFilter.value = statusFilter.value.filter((x) => x !== s);
        fetchAll();
      },
    });
  }
  if (requestedFrom.value) {
    caps.push({
      key: 'reqFrom',
      label: `${$t(m.requestedAtLabel)} ${$t(sharedMessages.from)}: ${
        requestedFrom.value
      }`,
      clear: () => {
        requestedFrom.value = '';
        fetchAll();
      },
    });
  }
  if (requestedTo.value) {
    caps.push({
      key: 'reqTo',
      label: `${$t(m.requestedAtLabel)} ${$t(sharedMessages.until)}: ${
        requestedTo.value
      }`,
      clear: () => {
        requestedTo.value = '';
        fetchAll();
      },
    });
  }
  if (obtainedFrom.value) {
    caps.push({
      key: 'obtFrom',
      label: `${$t(m.obtainedAtLabel)} ${$t(sharedMessages.from)}: ${
        obtainedFrom.value
      }`,
      clear: () => {
        obtainedFrom.value = '';
        fetchAll();
      },
    });
  }
  if (obtainedTo.value) {
    caps.push({
      key: 'obtTo',
      label: `${$t(m.obtainedAtLabel)} ${$t(sharedMessages.until)}: ${
        obtainedTo.value
      }`,
      clear: () => {
        obtainedTo.value = '';
        fetchAll();
      },
    });
  }
  for (const uuid of departmentFilter.value) {
    const dept = companyDepartments.value.find((d) => d.uuid === uuid);
    caps.push({
      key: `dept:${uuid}`,
      label: `${$t(sharedMessages.department)}: ${dept?.name ?? uuid}`,
      clear: () => {
        departmentFilter.value = departmentFilter.value.filter(
          (v) => v !== uuid
        );
        fetchAll();
      },
    });
  }
  for (const uuid of requesterFilter.value) {
    const opt = requesterOptions.value.find((o) => o.value === uuid);
    caps.push({
      key: `requester:${uuid}`,
      label: `${$t(sharedMessages.requester)}: ${opt?.text ?? uuid}`,
      clear: () => {
        requesterFilter.value = requesterFilter.value.filter((v) => v !== uuid);
        fetchAll();
      },
    });
  }
  return caps;
});

const isEligibleForCancel = (r: SupplierInquiryResponse): boolean =>
  r.status === 'NOT_OBTAINED';

// A row is selectable when it participates in at least one bulk action:
//   NOT_OBTAINED → bulk cancel eligible
//   OBTAINED     → bulk download eligible (対応完了 — customer-facing terminal
//                  state since PdM 2026-07-17 dropped COMPLETED)
// Everything else (CONTACTED / NOT_APPLICABLE / CANCELLED) yields no bulk
// action, so we disable the checkbox to keep the "n件選択" counter and the
// button gating meaningful.
const isRowSelectable = (r: SupplierInquiryResponse): boolean => {
  // Row is selectable iff at least one bulk action would apply:
  //   - NOT_OBTAINED → bulk cancel
  //   - OBTAINED WITH attachments → bulk download
  // An OBTAINED row with 0 attachments would be selectable but neither
  // action could act on it, so the "n件選択" counter would be misleading.
  if (r.status === 'NOT_OBTAINED') return true;
  if (r.status === 'OBTAINED') return (r.attachments?.length || 0) > 0;
  return false;
};

const selectablePageIds = computed(() =>
  pageItems.value.filter(isRowSelectable).map((r) => r.id)
);

const allSelectedOnPage = computed(
  () =>
    selectablePageIds.value.length > 0 &&
    selectablePageIds.value.every((id) => selectedIds.value.includes(id))
);

const partiallySelected = computed(
  () => selectedIds.value.length > 0 && !allSelectedOnPage.value
);

const isSelected = (id: string): boolean => selectedIds.value.includes(id);

// Selection that includes only cancel-eligible rows — used by the cancel button.
const cancellableSelection = computed(() =>
  items.value.filter(
    (r) => selectedIds.value.includes(r.id) && isEligibleForCancel(r)
  )
);

const toggleRow = (id: string) => {
  // Guard against selecting a disabled row through a stray external
  // trigger; v-simple-checkbox :disabled is enforced in the template too.
  const row = pageItems.value.find((r) => r.id === id);
  if (row && !isRowSelectable(row)) return;
  const idx = selectedIds.value.indexOf(id);
  if (idx >= 0) selectedIds.value.splice(idx, 1);
  else selectedIds.value.push(id);
};

const toggleAll = () => {
  if (allSelectedOnPage.value) {
    selectedIds.value = selectedIds.value.filter(
      (id) => !selectablePageIds.value.includes(id)
    );
  } else {
    const next = new Set([...selectedIds.value, ...selectablePageIds.value]);
    selectedIds.value = Array.from(next);
  }
};

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

const onTableOptions = (opts: { sortBy: string[]; sortDesc: boolean[] }) => {
  const newSortBy = opts.sortBy[0] ?? 'requested_at';
  const newSortDesc = opts.sortDesc[0] ?? true;
  if (newSortBy !== sortBy.value || newSortDesc !== sortDesc.value) {
    sortBy.value = newSortBy;
    sortDesc.value = newSortDesc;
    currentPage.value = 1;
    fetchAll();
  }
};

const fetchAll = async () => {
  loading.value = true;
  try {
    const [listRes, summaryRes] = await Promise.all([
      generalApi.getSupplierInquiryRequests({
        status: statusFilter.value.length > 0 ? statusFilter.value : undefined,
        q: committedSearchQuery.value || undefined,
        requested_from: requestedFrom.value || undefined,
        requested_to: requestedTo.value || undefined,
        obtained_from: obtainedFrom.value || undefined,
        obtained_to: obtainedTo.value || undefined,
        departmentIds:
          departmentFilter.value.length > 0
            ? departmentFilter.value
            : undefined,
        requestedByUserIds:
          requesterFilter.value.length > 0 ? requesterFilter.value : undefined,
        page: currentPage.value - 1,
        limit: itemsPerPage.value,
        sort_by: sortBy.value,
        sort_desc: sortDesc.value,
      }),
      generalApi.getSupplierInquirySummary({
        status: statusFilter.value.length > 0 ? statusFilter.value : undefined,
        q: committedSearchQuery.value || undefined,
        requested_from: requestedFrom.value || undefined,
        requested_to: requestedTo.value || undefined,
        obtained_from: obtainedFrom.value || undefined,
        obtained_to: obtainedTo.value || undefined,
      }),
    ]);
    items.value = listRes.data.items;
    total.value = listRes.data.total;
    summary.value = summaryRes.data;
    const present = new Set(items.value.map((r) => r.id));
    selectedIds.value = selectedIds.value.filter((id) => present.has(id));
    if (currentPage.value > totalPages.value) currentPage.value = 1;
  } catch {
    showNotification($t(m.loadError) as string, 'error');
  } finally {
    loading.value = false;
  }
};

const confirmCancel = async () => {
  const targets = cancellableSelection.value;
  if (targets.length === 0) return;
  cancelling.value = true;
  try {
    await Promise.all(
      targets.map((r) => generalApi.cancelSupplierInquiryRequest(r.id))
    );
    showNotification($t(m.cancelSuccess) as string, 'success');
    selectedIds.value = [];
    cancelDialog.value = false;
    await fetchAll();
  } catch (e: unknown) {
    const detail = (e as { response?: { data?: { detail?: string } } })
      ?.response?.data?.detail;
    showNotification(detail || ($t(m.cancelError) as string), 'error');
  } finally {
    cancelling.value = false;
  }
};

const onLocalFilterChange = () => {
  currentPage.value = 1;
  fetchAll();
};

// Scroll all the way to the top on any pagination-driven refetch.
// Vuetify's v-main uses padding-top for the fixed AppBar + V2Banner,
// so the underlying scroll container is window / documentElement /
// body. Earlier version used `tableWrapper.scrollIntoView(...)` — that
// only aligned the table's top with the viewport top, which is
// underneath the sticky bars, so it looked like nothing happened
// when the table was already partly on screen. Hit every plausible
// scroller and use `auto` (no smooth) so the customer lands at the
// top before the freshly-rendered rows draw.
const scrollListToTop = () => {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
};

const onPageChange = async () => {
  await fetchAll();
  scrollListToTop();
};

const onItemsPerPageChange = async () => {
  currentPage.value = 1;
  await fetchAll();
  scrollListToTop();
};

const runSearch = () => {
  committedSearchQuery.value = searchQuery.value.trim();
  currentPage.value = 1;
  fetchAll();
};

const onSelectAllAcrossPages = async () => {
  // Fetch all matching IDs in one go and stage them as selected.
  try {
    const { data } = await generalApi.getSupplierInquiryRequests({
      status: statusFilter.value.length > 0 ? statusFilter.value : undefined,
      q: committedSearchQuery.value || undefined,
      requested_from: requestedFrom.value || undefined,
      requested_to: requestedTo.value || undefined,
      obtained_from: obtainedFrom.value || undefined,
      obtained_to: obtainedTo.value || undefined,
      departmentIds:
        departmentFilter.value.length > 0 ? departmentFilter.value : undefined,
      requestedByUserIds:
        requesterFilter.value.length > 0 ? requesterFilter.value : undefined,
      page: 0,
      limit: 200,
    });
    // Only stage rows that actually participate in a bulk action so the
    // "全SDS N件を選択" affordance is honest — otherwise it inflates the
    // counter with CONTACTED / OBTAINED / NOT_APPLICABLE / CANCELLED
    // rows the user can't act on.
    selectedIds.value = data.items.filter(isRowSelectable).map((r) => r.id);
  } catch {
    showNotification($t(m.loadError) as string, 'error');
  }
};

// Trigger a browser download without going through window.open — needed
// for the multi-select flow because window.open in a loop is
// popup-blocked after the first entry, so only 1 of N would open.
// A programmatic anchor click is treated as a user-initiated download
// and is not popup-blocked, so all URLs fire.
const triggerDownload = (url: string, filename?: string): void => {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener noreferrer';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// Mirror of the backend's _sanitize_zip_name for anchor.download.
// Strips path components + CR/LF so a hostile file_name / sds.name
// (both user-supplied) can't force weird cross-browser save
// behavior. Empty input returns empty; caller falls back.
const sanitizeDownloadName = (name: string | null | undefined): string => {
  if (!name) return '';
  // Handle both POSIX and Windows separators — some uploaders emit
  // backslash paths.
  const parts = name.replace(/\\/g, '/').split('/');
  const base = parts[parts.length - 1] || '';
  return base.replace(/[\r\n]/g, '');
};

// Step 6 of the PdM service flow: customer downloads the attachments
// (any file type — PdM 2026-07-22 broadened this from "the obtained
// SDS" to N arbitrary files) for each selected row. PdM 2026-07-17
// dropped COMPLETED so OBTAINED (対応完了) is now the customer-facing
// terminal state. Rows in earlier states are skipped with a warning.
//
// Multi-select returns a single ZIP from the backend rather than N
// separate downloads, so the customer sees one browser download prompt
// and Chrome / Edge do not gate on "block automatic downloads".
// Rows in the current selection that actually have a downloadable file.
// Drives both the bulk-download button's disabled state / tooltip and the
// downstream API request. A row is eligible when status === OBTAINED AND
// at least one attachment is present (PdM 2026-07-08 allows the operator
// to reach 対応完了 without uploading; PdM 2026-07-22 replaced the single
// obtained_file_name column with a 1:N supplier_inquiry_attachment
// relation — the check moves to attachments.length > 0).
const downloadableSelection = computed(() =>
  items.value.filter(
    (r) =>
      selectedIds.value.includes(r.id) &&
      r.status === 'OBTAINED' &&
      (r.attachments?.length || 0) > 0
  )
);

// Bulk-download flow: click the footer button → confirmation dialog
// (Figma 38176:133878) → confirmDownload posts the ZIP request. Dialog
// exists to make the "対応完了 + file only" filtering visible to the
// customer before they commit — otherwise they'd wonder why 12 selected
// rows produced a 3-file ZIP.
const onDownloadLatest = () => {
  if (downloadableSelection.value.length === 0) {
    showNotification($t(m.noDownloadableInSelection), 'warning');
    return;
  }
  downloadDialog.value = true;
};

const confirmDownload = async () => {
  const downloadable = downloadableSelection.value;
  if (downloadable.length === 0) {
    downloadDialog.value = false;
    return;
  }
  downloading.value = true;
  try {
    const { data } = await generalApi.bulkDownloadSupplierInquiryFiles(
      downloadable.map((r) => r.id)
    );
    const url = URL.createObjectURL(data);
    triggerDownload(
      url,
      `サプライヤー問い合わせ_${new Date().toISOString().slice(0, 10)}.zip`
    );
    // Revoke asynchronously so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    downloadDialog.value = false;
  } catch (e: unknown) {
    // Same blob-response caveat as onDownloadSingle — read the FastAPI
    // error detail out of the Blob so a 413 payload-too-large surfaces
    // as its specific message ("件数を減らして…") instead of the generic
    // 「ダウンロードに失敗しました」.
    const detail = await blobErrorDetail(e);
    showNotification(detail || $t(m.bulkDownloadFailed), 'error');
  } finally {
    downloading.value = false;
  }
};

const onDownloadSingle = async (r: SupplierInquiryResponse) => {
  // Guard against overlapping downloads: only one row's download
  // may be in flight at a time. The earlier `!== r.id` check only
  // blocked re-clicking the SAME row — the user could still fire
  // row A, hover row B, click before A completed, and clobber
  // downloadingSingleId (leaving A's spinner permanently frozen
  // and B's finally clearing the state prematurely).
  if (downloadingSingleId.value !== null) return;
  downloadingSingleId.value = r.id;
  try {
    const res = await generalApi.downloadSupplierInquiryFile(r.id);
    // PdM 2026-07-22: single attachment → the file itself; multiple →
    // a ZIP built by the backend. Both arrive as a blob. Filename is
    // computed client-side — matches the pattern used by every other
    // blob-response download in this repo (CREATE SIMPLE export,
    // bulkDownloadSupplierInquiryFiles, etc.) and avoids the CORS
    // `expose_headers` gap that would silently null-out
    // Content-Disposition when read from JS.
    const attachments = r.attachments || [];
    // Both file_name and sds.name are user-controlled (uploader-
    // supplied and customer-tenant-supplied respectively). Browsers
    // handle path separators / control chars in the `download`
    // attribute inconsistently, so sanitize to a basename before
    // passing to the anchor — mirrors the backend's _sanitize_zip_name.
    const filename =
      attachments.length > 1
        ? sanitizeDownloadName(`${r.sds?.name || 'SDS'}.zip`)
        : sanitizeDownloadName(attachments[0]?.file_name) || 'download';
    const url = URL.createObjectURL(res.data);
    triggerDownload(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e: unknown) {
    // The download endpoint uses `responseType: 'blob'`, so on error
    // axios sets `err.response.data` to the response body as a Blob
    // — reading `.detail` off it always yields undefined. Read the
    // blob as text and try to parse a FastAPI-shaped JSON error
    // ({detail: string}) before falling back to the generic toast.
    const detail = await blobErrorDetail(e);
    showNotification(
      detail || $t(m.downloadFailedFor, { name: r.sds?.name || 'SDS' }),
      'error'
    );
  } finally {
    downloadingSingleId.value = null;
  }
};

// Read a FastAPI-shape error detail out of an axios error whose
// underlying request used `responseType: 'blob'`. Returns undefined
// if the shape doesn't match — caller falls back to a generic toast.
const blobErrorDetail = async (e: unknown): Promise<string | undefined> => {
  const err = e as { response?: { data?: unknown } };
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return undefined;
  try {
    const text = await data.text();
    const parsed = JSON.parse(text) as { detail?: unknown };
    return typeof parsed.detail === 'string' ? parsed.detail : undefined;
  } catch {
    return undefined;
  }
};

const onCancelSingle = (r: SupplierInquiryResponse) => {
  selectedIds.value = [r.id];
  cancelDialog.value = true;
};

onMounted(() => {
  // Fetch the department + user pickers alongside the initial list. Errors
  // are swallowed inside the composables (they log to console); an empty
  // dropdown is a survivable state so we don't block list load on either.
  Promise.all([
    getCompanyDepartments(),
    getCompanyDepartmentTree(),
    getCompanyUsers(),
  ]).catch(() => {
    /* pickers stay empty */
  });
  fetchAll();
});

return { __sfc: true,$t, m, STATUS_LABEL, STATUS_CHIP, COUNTER_STATUSES, hasPermission, PERMISSIONS, canDownloadResults, loading, items, tableWrapper, hoveredItem, overlayTop, overlayHeight, hoveredRowEl, setHoveredRow, onTableMouseOver, onTableMouseLeave, onOverlayWheel, total, summary, searchQuery, committedSearchQuery, statusFilter, statusFilterDraft, isOpenStatusMenu, requestedFrom, requestedTo, obtainedFrom, obtainedTo, departmentFilter, requesterFilter, companyDepartments, getCompanyDepartments, departmentTree, getCompanyDepartmentTree, companyUsers, getCompanyUsers, onDepartmentFilterChange, requesterOptions, isOpenRequestedFromPicker, isOpenRequestedToPicker, isOpenObtainedFromPicker, isOpenObtainedToPicker, STATUS_FILTER_OPTIONS, openStatusMenu, toggleStatusDraft, clearStatusDraft, commitStatusFilter, clearAllFilters, locale, selectedIds, itemsPerPage, currentPage, sortBy, sortDesc, cancelDialog, cancelling, downloadDialog, downloading, downloadingSingleId, statusInfoDialog, notification, showNotification, statusCount, formatDate, chemicanCommentFor, requesterLabel, totalPages, pageOptions, pageItems, activeFilters, isEligibleForCancel, isRowSelectable, selectablePageIds, allSelectedOnPage, partiallySelected, isSelected, cancellableSelection, toggleRow, toggleAll, onTableOptions, fetchAll, confirmCancel, onLocalFilterChange, scrollListToTop, onPageChange, onItemsPerPageChange, runSearch, onSelectAllAcrossPages, triggerDownload, sanitizeDownloadName, downloadableSelection, onDownloadLatest, confirmDownload, onDownloadSingle, blobErrorDetail, onCancelSingle, DepartmentTreeSelect, sharedMessages, formatDatePickerDay }
}

})